import type { FC } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { OnChangePlugin as LexicalOnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import type { LexicalEditor, TextNode } from 'lexical';
import {
  $addUpdateTag,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
} from 'lexical';

import { isCreatingAction } from 'components/molecules/LexicalEditor/plugins/ActionPicker/utils';
import { SKIP_DOM_SELECTION_TAG } from '../../constants';
import { ACTION_TAG_REGEXES } from '../../features/action/constants';
import { $createActionNode } from '../../features/action/nodes/ActionNode';
import { $deserializeFromRawText } from '../../utils/deserialize';
import { $serializeToRawText } from '../../utils/serialize';

type LexicalOnChangePluginProps = Parameters<typeof LexicalOnChangePlugin>[0];

type Props = {
  value: string | undefined;
  onChange: (value: string) => void;
  deserialize?: typeof $deserializeFromRawText;
  serialize?: typeof $serializeToRawText;
};

/**
 * Processes text nodes within a paragraph and replaces action tag patterns with ActionNodes
 * @param {LexicalEditor} editor
 * @param {string} nodeKey
 * @return {void}
 */
function processTextNodesForActionTags({
  editor,
  nodeKey,
}: {
  editor: LexicalEditor;
  nodeKey: string;
}) {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  editor.update(() => {
    $addUpdateTag(SKIP_DOM_SELECTION_TAG);

    const node = $getNodeByKey(nodeKey);
    if (!node) {
      return;
    }

    const textNodes: TextNode[] = [];

    // Collect text nodes from either TextNode or ParagraphNode
    if ($isTextNode(node)) {
      textNodes.push(node);
    } else if ($isParagraphNode(node)) {
      node.getChildren().forEach(child => {
        if ($isTextNode(child)) {
          textNodes.push(child);
        }
      });
    }

    // Process each text node to find action tags
    for (const textNode of textNodes) {
      const text = textNode.getTextContent();
      // Find all action tag matches
      let matches: RegExpMatchArray[] = [];

      // Apply each regex pattern from the array and collect all matches
      ACTION_TAG_REGEXES.forEach(regexPattern => {
        const regexMatches = Array.from(
          text.matchAll(new RegExp(regexPattern, 'g')),
        );
        matches = [...matches, ...regexMatches];
      });

      if (matches.length > 0) {
        // If we found matches, process them from right to left to maintain index integrity
        for (let i = matches.length - 1; i >= 0; i--) {
          const match = matches[i];
          const matchText = match[0];
          const prefix = match[1];
          const id = match[2];
          const startOffset = match.index || 0;
          const endOffset = startOffset + matchText.length;

          // Split the text node at the match boundaries
          if (startOffset > 0) {
            textNode.splitText(startOffset);
          }
          const actionTagTextNode =
            startOffset > 0
              ? (textNode.getNextSibling() as TextNode)
              : textNode;

          if (endOffset < actionTagTextNode.getTextContent().length) {
            actionTagTextNode.splitText(matchText.length);
          }

          // Replace the text node with an action node
          const actionNode = $createActionNode({
            prefix,
            id,
          });

          actionTagTextNode.replace(actionNode);
        }
      }
    }
  });
}

const NewOnChangePlugin: FC<Props> = ({
  value,
  onChange,
  deserialize = $deserializeFromRawText,
  serialize = $serializeToRawText,
}) => {
  const [editor] = useLexicalComposerContext();
  const isExternalUpdateRef = useRef(false);
  const lastValueRef = useRef<string | undefined>(value);

  // Initial value setup on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  useLayoutEffect(() => {
    if (value !== undefined) {
      isExternalUpdateRef.current = true;

      editor.update(() => {
        $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        const nodes = deserialize(value);
        $getRoot()
          .clear()
          .append(...nodes);
        // Clear selection to prevent auto-focus on mount (React 19 compatibility)
        $setSelection(null);
      });

      lastValueRef.current = value;
    }

    // Reset the flag after the editor update completes
    setTimeout(() => {
      isExternalUpdateRef.current = false;
    }, 0);
  }, []);

  // Handle external value updates (controlled component behavior)
  useEffect(() => {
    if (value !== lastValueRef.current) {
      isExternalUpdateRef.current = true;

      editor.update(() => {
        $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        const nodes = deserialize(value);
        $getRoot()
          .clear()
          .append(...nodes);
        // Clear selection to prevent auto-focus on external updates
        $setSelection(null);
      });

      lastValueRef.current = value;

      // Reset the flag after the editor update completes
      setTimeout(() => {
        isExternalUpdateRef.current = false;
      }, 0);
    }
  }, [value, editor, deserialize]);

  const handleOnChange: LexicalOnChangePluginProps['onChange'] =
    editorState => {
      // Skip processing if this change was triggered by an external update
      if (isExternalUpdateRef.current) {
        return;
      }

      if (isCreatingAction(editor._config.namespace)) {
        return;
      }

      editorState.read(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          processTextNodesForActionTags({
            editor,
            nodeKey: anchorNode.getKey(),
          });
        }

        // Only trigger onChange if the content actually changed
        const serializedContent = serialize();
        if (serializedContent !== lastValueRef.current) {
          lastValueRef.current = serializedContent;
          onChange(serializedContent);
        }
      });
    };

  return <LexicalOnChangePlugin onChange={handleOnChange} />;
};

export default NewOnChangePlugin;
